import { buildUrl } from '@repo/toolkit/build-url'
import { OlModeProps, OlModeTable } from '@repo/toolkit/ol-mode-table-helper'
import { useNavigator } from '@repo/toolkit/use-navigator'
import { Column } from '@repo/ui/data-table'
import { PlusIcon } from '@repo/ui/icons'
import InnerBox from '@repo/ui/inner-box'

import { API, constructApiUrl } from '../../../../api/apis.config'
import { getOlPermissions } from '../../../../lib/constants/ol-permissions'
import { Paths } from '../../../../lib/constants/path'
import useAuth from '../../../../lib/hooks/use-auth'
import useTranslate from '../../../../lib/hooks/use-translate'
import { CoachCompany } from '../../../../lib/types/models/ol/coach-company/coach-company'
import { Ol } from '../../../../lib/types/models/ol/ol'
import { DataProps } from '../../../../lib/types/utils'
import { formatPhoneNumber } from '../../../../utils/functions/format-phone-number'
import DynamicDataTable, { TableAction } from '../../../elements/dynamic-table'
import { olNameColumn } from '../common/ol-columns'

type Props = OlModeProps & { olId?: number }

const OlCoachCompanyListPage = (props: DataProps<Ol, Props> | Props) => {
  // Handle both DataProps pattern and direct props pattern
  const isDataProps = 'data' in props || 'olId' in props
  const olId = isDataProps && 'olId' in props ? props.olId : undefined
  const ol = isDataProps && 'data' in props ? props.data : undefined

  const { user } = useAuth()
  const { navigateWithEvent, navigate } = useNavigator()
  const { t } = useTranslate()

  const mode = new OlModeTable(props as OlModeProps)

  const endpoint = mode.endpoint({
    ol: (olId) => constructApiUrl(API.ols.getCoachCompanies.url, { olId }),
    global: API.ols.getAllCoachCompanies.url
  })

  const baseColumns: Column<CoachCompany>[] = [
    {
      accessorKey: 'name',
      header: t('coach.company.table.name'),
      enableSorting: true
    },
    {
      accessorKey: 'phoneNumber',
      columnHeaderClassName: 'w-1/2',
      header: t('coach.company.table.phoneNumber'),
      cell: ({ row }) => formatPhoneNumber(row.original.phoneNumber)
    },
    {
      accessorKey: 'comments',
      header: t('coach.company.table.comments'),
      enableSorting: true,
      cell: ({ row }) => row.original.comments || '-'
    }
  ]

  const columns = mode.columns(baseColumns, [olNameColumn<CoachCompany>()])

  const onRowClick = (coachCompany: CoachCompany, event?: React.MouseEvent) => {
    if (event) {
      navigateWithEvent(
        buildUrl(Paths.OL_COACH_COMPANIES_VIEW_PAGE, {
          id: coachCompany.id,
          olId: coachCompany.ol.id
        }),
        event
      )
    }
  }

  const actions: TableAction<CoachCompany, undefined>[] | undefined = mode.isOl()
    ? [
        {
          type: 'global',
          label: t('coach.company.table.add.coach.company'),
          icon: PlusIcon,
          fn: () => navigate(buildUrl(Paths.OL_COACH_COMPANIES_CREATE_PAGE, { olId: olId! }))
        }
      ]
    : undefined

  const olPermissions = ol ? getOlPermissions({ role: user.role, type: ol.type }) : null

  return (
    <InnerBox className="rounded-2xl p-0">
      <DynamicDataTable<CoachCompany, null>
        title={'Compagnies de bus'}
        deleteMessage={{
          name: 'compagnies de bus',
          attributeName: 'name',
          deleteAll: 'Êtes-vous sûr de vouloir supprimer toutes les compagnies de bus?'
        }}
        permissions={{ delete: olPermissions?.['coach-companies'] ?? false }}
        columns={columns}
        allowSearchParam
        onRowClick={onRowClick}
        actions={olPermissions?.['coach-companies'] ? actions : undefined}
        selectable={olPermissions?.['coach-companies'] ?? false}
        endpoint={endpoint}
      />
    </InnerBox>
  )
}

export default OlCoachCompanyListPage
